import { useMutation, useQuery } from '@apollo/react-hooks'
import {
  IonButton,
  IonButtons,
  IonCheckbox,
  IonContent,
  IonHeader,
  IonIcon,
  IonItem,
  IonLabel,
  IonModal,
  IonSkeletonText,
  IonTitle,
  IonToggle,
  IonToolbar
} from '@ionic/react'
import dayjs from 'dayjs'
import IBAN from "iban"
import { close, rocketOutline } from 'ionicons/icons'
import React, { useState } from 'react'
import { connect } from 'react-redux'
import { ID } from '../graphql/id.type'
import {
  CREATE_QUOTE_SIGNATURE_MUTATION,
  CreateQuoteSignatureData,
  CreateQuoteSignatureVariables
} from '../graphql/mutation/create.quote.signature'
import { QUOTE_QUERY, QuoteData, QuoteVariables } from '../graphql/query/quote'
import { State } from '../store'
import { addNotification, addNotificationType } from '../store/notification'
import { AddressCard } from './card/address.card'
import FormEmail from './form/email'
import FormIban from './form/iban'
import FormMobile from './form/mobile'
import FormPaymentTerm from './form/payment.term'
import FormRiskStartDate from './form/risk.start.date'
import Note from './note'

interface StateProps {
  accessKey: string
}

interface DispatchProps {
  addNotification: addNotificationType
}

interface OwnProps {
  id: ID
  gf: string
  visible: boolean
  onHide: () => any
}

type Props = StateProps & DispatchProps & OwnProps

const QuoteSignatureModalInternal: React.FC<Props> = (props) => {
  const [step, setStep] = useState(0)
  const [iban, setIban] = useState('')
  const [email, setEmail] = useState('')
  const [mobile, setMobile] = useState('')
  const [riskStartDate, setRiskStartDate] = useState('')
  const [confirmed, setConfirmed] = useState(false);
  const [canceledContract, setCanceledContract] = useState(false);
  const [otherContract, setOtherContract] = useState(false);
  const [electronicCommunication, setElectronicCommunication] = useState(true);
  const [paymentTerm, setPaymentTerm] = useState('monthly')

  const [requestSignature] = useMutation<CreateQuoteSignatureData, CreateQuoteSignatureVariables>(CREATE_QUOTE_SIGNATURE_MUTATION)

  const {data, loading, error} = useQuery<QuoteData, QuoteVariables>(QUOTE_QUERY, {
    variables: {
      id: props.id,
      accessKey: props.accessKey,
    },
    onCompleted(data) {
      setIban(data.quote.iban || '')
      setEmail(data.quote.person.email)
      setMobile(data.quote.person.mobile)
      setRiskStartDate(data.quote.riskStartDate || dayjs().add(1, 'month').startOf('month').format('YYYY-MM-DD'))
    }
  })

  if (!props.visible) {
    return null
  }

  const onClose = () => {
    setStep(0)
    props.onHide()
  }

  const onOpenConfirm = () => {
    if (!iban) {
      props.addNotification({
        color: 'danger',
        message: 'IBAN fehlt'
      })
      return
    }
    if (!IBAN.isValid(iban)) {
      props.addNotification({
        message: 'IBAN ist nicht gültig!',
        color: 'danger',
      })
      return
    }

    if (!email) {
      props.addNotification({
        color: 'danger',
        message: 'Email fehlt'
      })
      return
    }
    if (!/[^@]+@[^@]+\.[^@]+$/.test(email)) {
      props.addNotification({
        message: 'E-Mail ungültig!',
        color: 'danger',
      })
      return
    }

    if (!mobile) {
      props.addNotification({
        color: 'danger',
        message: 'Mobil Nummer fehlt'
      })
      return
    }
    if (!/^(00|\+)43[0-9 \-]+/.test(mobile)) {
      props.addNotification({
        message: 'Mobil Nummer muss mit +43 beginnen!',
        color: 'danger',
      })
      return
    }

    if (!riskStartDate) {
      props.addNotification({
        color: 'danger',
        message: 'Start Datum fehlt'
      })
      return
    }

    if (!paymentTerm) {
      props.addNotification({
        color: 'danger',
        message: 'Zahlweise fehlt'
      })
      return
    }

    setStep(1)
  }

  const onConfirm = async () => {
    if (!confirmed) {
      props.addNotification({
        color: 'danger',
        message: 'Lesebestätigung erforderlich'
      })
      return
    }

    setStep(2)

    try {
      await requestSignature({
        variables: {
          quote: props.id,
          accessKey: props.accessKey,
          signature: {
            iban,
            email,
            mobile,
            paymentTerm,
            riskStartDate,
            canceledContract,
            otherContract,
            electronicCommunication,
          },
        },
      })

      props.addNotification({
        message: 'Wir warten auf ihre Unterschrift!',
        color: 'success',
      })
    } catch (e) {
      setStep(1)

      props.addNotification({
        message: 'Konnte SMS nicht senden, bitte später erneut versuchen',
        color: 'danger',
      })
    }
  }

  const content = () => {
    if (step === 0) {
      if (loading) {
        return (
          <IonContent className="ion-padding">
            <IonSkeletonText animated style={{ width: '60%' }} />
            <IonSkeletonText animated />
            <IonSkeletonText animated style={{ width: '88%' }} />
            <IonSkeletonText animated style={{ width: '70%' }} />
            <IonSkeletonText animated style={{ width: '60%' }} />
          </IonContent>
        )
      }

      return (
        <IonContent className="ion-padding">
          <Note>
            <h1>Fast geschafft</h1>
            Auf der nächsten Seite erhalten sie genaue Infos zu den nächsten Schritten.
          </Note>

          <FormIban value={iban} onChange={setIban} />
          <FormEmail value={email} onChange={setEmail} />
          <FormMobile value={mobile} onChange={setMobile} />
          <FormPaymentTerm value={paymentTerm} onChange={setPaymentTerm} />
          <FormRiskStartDate value={riskStartDate} onChange={setRiskStartDate}/>

          <AddressCard address={data?.quote.address} condensed />

          <IonItem>
            <IonLabel className={'ion-text-wrap'}>
              Wurde dem Versicherungsnehmer und/oder einer versicherten Person eine Versicherung gekündigt, abgelehnt oder ein Vertrag einvernehmlich aufgelöst?
            </IonLabel>
            <IonToggle
              checked={canceledContract}
              onIonChange={e => setCanceledContract(e.detail.checked)}
              style={{
                '::before': {
                  content: 'ja'
                }
              }}
            />
          </IonItem>

          <IonItem>
            <IonLabel className={'ion-text-wrap'}>
              Bestehen für die zu versichernden Personen und/oder Sachen noch andere Versicherungsanträge, oder haben Sie welche beantragt?
            </IonLabel>
            <IonToggle checked={otherContract} onIonChange={e => setOtherContract(e.detail.checked)} />
          </IonItem>

          <IonItem>
            <IonLabel className={'ion-text-wrap'}>
              Ich bin mit weiterer Kommunikation in allen Anliegen um die Polizze per E-Mail und Telefon nach dem Telekommunikationsgesetz einverstanden.
            </IonLabel>
            <IonToggle checked={electronicCommunication} onIonChange={e => setElectronicCommunication(e.detail.checked)} />
          </IonItem>

          <IonButton
            expand={'full'}
            onClick={() => onOpenConfirm()}
          >
            Weiter
          </IonButton>
        </IonContent>
    )
    }

    if (step === 1) {
      return (
        <IonContent className="ion-padding">
          <Note>
            <h1>Wunderbar</h1>
            Bitte lesen sie die folgenden Schritte durch um den Antrag abschicken zu können.
          </Note>

          <ul>
            <li style={{marginBottom: 16}}>
              Wir senden ihnen an ihre Handynummer eine SMS für die Unterschrift
            </li>
            <li style={{marginBottom: 16}}>
              Das Dokument beinhaltet mehrere Unterschriften:

              <ul>
                <li>
                  SEPA Mandat
                </li>
                <li>
                  Polizzen Antrag
                </li>
              </ul>
            </li>
            <li style={{marginBottom: 16}}>
              Der Versicherungsschutz tritt frühestens mit polizzierung ein
            </li>
            <li style={{marginBottom: 16}}>
              Mit ihrer Unterschrift gehen sie einen Kostenpflichtingen Versicherungsvertrag ein.
            </li>
          </ul>
          <IonItem lines={'none'}>
            <IonLabel>Ich habe die Schritte gelesen</IonLabel>
            <IonCheckbox checked={confirmed} onIonChange={e => setConfirmed(e.detail.checked)} />
          </IonItem>
          <IonButton
            expand={'full'}
            onClick={() => onConfirm()}
          >
            Jetzt Unterschreiben
          </IonButton>
        </IonContent>
      )
    }

    if (step === 2) {
      return (
        <IonContent className="ion-padding">
          <IonIcon icon={rocketOutline} style={{
            fontSize: 160,
            display: 'block',
            marginLeft: 'auto',
            marginRight: 'auto',
            marginTop: 32,
          }}/>

          <Note>
            <h1>Anfrage unterwegs</h1>
            Wir verarbeiten noch den Antrag mit ihren Daten und senden ihnen dann die SMS für die Unterschrift zu.
          </Note>

          <IonButton
            expand={'full'}
            onClick={() => onClose()}
          >
            Zur Startseite
          </IonButton>
        </IonContent>
      )
    }

    return (
      <div>
        Oh, hier ist etwas schief gegangen.
      </div>
    )
  }

  if (!loading && error) {
    props.addNotification({
      color: 'danger',
      message: 'Wir können gerade nicht auf das Angebot zugreifen'
    })
    return null
  }

  return (
    <IonModal isOpen={props.visible}>
      <IonHeader>
        <IonToolbar color={'primary'}>
          <IonButtons slot="start">
            <IonButton onClick={() => onClose()}>
              <IonIcon slot="icon-only" icon={close} />
            </IonButton>
          </IonButtons>
          <IonTitle>Geschäftsfall {props.gf}</IonTitle>
        </IonToolbar>
      </IonHeader>

      {content()}
    </IonModal>
  )
}

export const QuoteSignatureModal = connect<StateProps, DispatchProps, OwnProps, State>(
  ({history}, props) => ({
    accessKey: history.list[props.id]?.accessKey || 'legacy',
  }),
  {
    addNotification,
  }
)(QuoteSignatureModalInternal)
